// @ts-check
import log from 'loglevel';

// [LAMS-2441] Per-user video playback preferences (localStorage), keyed by userId, so choices survive
// reloads, course switches, and sessions — applied to every course. Holds `playbackRate` and `quality`.
const KEY = 'learncraft_video_preferences_by_user';

/** @typedef {{ playbackRate?: number, quality?: 'auto'|number }} VideoPreferences */

/** @returns {Record<string, VideoPreferences>} */
function readMap() {
  try {
    const raw = localStorage.getItem(KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    // Guard corrupt/hand-edited values so callers never crash on parse.
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (e) {
    log.debug('[videoPreferencesStorage] failed to read/parse; ignoring', e);
    return {};
  }
}

/**
 * All stored preferences for a user. Missing userId / invalid data → {}.
 * @param {string} userId
 * @returns {VideoPreferences}
 */
export function getVideoPreferences(userId) {
  if (!userId) return {};
  const prefs = readMap()[userId];
  return prefs && typeof prefs === 'object' ? prefs : {};
}

/**
 * Merge a partial patch into a user's preferences — per-field, so writing one key never clobbers siblings.
 * @param {string} userId
 * @param {Partial<VideoPreferences>} patch
 */
export function setVideoPreferences(userId, patch) {
  if (!userId || !patch || typeof patch !== 'object') return;
  try {
    const map = readMap();
    const current = map[userId] && typeof map[userId] === 'object' ? map[userId] : {};
    map[userId] = { ...current, ...patch };
    localStorage.setItem(KEY, JSON.stringify(map));
  } catch (e) {
    // Best-effort: storage full/disabled (e.g. Safari private mode). Don't break playback.
    log.debug('[videoPreferencesStorage] failed to persist; ignoring', e);
  }
}

// --- playbackRate (starter field; quality etc. follow this same typed-wrapper pattern) ---

/**
 * @param {string} userId
 * @returns {number|null} remembered rate, or null when absent/invalid
 */
export function getPlaybackRate(userId) {
  const rate = getVideoPreferences(userId).playbackRate;
  return typeof rate === 'number' && Number.isFinite(rate) && rate > 0 ? rate : null;
}

/**
 * @param {string} userId
 * @param {number} rate
 */
export function setPlaybackRate(userId, rate) {
  // Skip bad values so we never persist junk (userId is guarded inside setVideoPreferences).
  if (typeof rate !== 'number' || !Number.isFinite(rate) || rate <= 0) return;
  setVideoPreferences(userId, { playbackRate: rate });
}

// --- quality ('auto' or a height number like 720) ---

/**
 * Deliberately looser than the player's own menu allowlist (lams: `QUALITY_OPTIONS`) so odah doesn't
 * have to track it — a stored height the player no longer offers is dropped on its side.
 * @param {unknown} quality
 * @returns {quality is 'auto'|number}
 */
export function isValidQuality(quality) {
  return (
    quality === 'auto' || (typeof quality === 'number' && Number.isFinite(quality) && quality > 0)
  );
}

/**
 * @param {string} userId
 * @returns {'auto'|number|null} remembered quality, or null when absent/invalid
 */
export function getQuality(userId) {
  const q = getVideoPreferences(userId).quality;
  return isValidQuality(q) ? q : null;
}

/**
 * @param {string} userId
 * @param {'auto'|number} quality
 */
export function setQuality(userId, quality) {
  // Skip bad values so we never persist junk. Log it: a shape mismatch with the player's payload
  // would otherwise look like the feature silently never remembering anything.
  if (!isValidQuality(quality)) {
    log.debug('[videoPreferencesStorage] ignoring invalid quality', quality);
    return;
  }
  setVideoPreferences(userId, { quality });
}
